import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { FileCheck2, Printer, X, Sparkles, TrendingUp, TrendingDown, Minus, Loader2, Activity } from "lucide-react";
import { toast } from "sonner";
import { patientApi } from "../services/api";

// Formatare dată dd.mm.yyyy
const fmt = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const addMonths = (d, months) => {
  const nd = new Date(d);
  nd.setMonth(nd.getMonth() + months);
  return nd;
};

const nonEmpty = (v) => v !== undefined && v !== null && String(v).trim() !== "";

// Reproducere logică Atelier 4: extrage TOATE recomandările active pe baza câmpurilor salvate
// (păstrat sincronizat cu Atelier4Button.jsx recomandari useMemo)
const collectAtelier4Recomandari = (source) => {
  const a4 = source?.atelier_4 || {};
  const out = [];
  if (a4.tegumente_status === "Modificat" && a4.teg_dermatologie) out.push("Se recomandă control și tratament de specialitate serviciu Dermatologie");
  if (a4.tesut_monitorizare) out.push("Se recomandă monitorizarea greutății corporale");
  if (a4.tesut_nutritie) out.push("Se recomandă consult și tratament de specialitate serviciu Nutriție");
  if (a4.tesut_endocrin) out.push("Se recomandă consult și tratament de specialitate serviciu Endocrinologie");
  if (a4.locomotor_status === "Modificat") {
    if (a4.loc_repaus) out.push(`Se recomandă repaus segmentar ${a4.loc_repaus_sapt || "___"} săptămâni`);
    if (a4.loc_investigatii) out.push(`Se recomandă investigații imagistice suplimentare${a4.loc_investigatii_text ? ": " + a4.loc_investigatii_text : ""}`);
    if (a4.loc_reevaluare) out.push(`Se recomandă reevaluare în ${a4.loc_reevaluare_sapt || "___"} săptămâni`);
    if (a4.loc_ortopedie_ped) out.push("Se recomandă consult și tratament de specialitate serviciu Ortopedie pediatrică");
    if (a4.loc_ortopedie) out.push("Se recomandă consult și tratament de specialitate serviciu Ortopedie");
  }
  if (a4.respirator_status === "Modificat") {
    if (a4.resp_pediatrie) out.push("Se recomandă consult și tratament de specialitate serviciu Pediatrie");
    if (a4.resp_rx) out.push("Se recomandă RX cord-pulmon");
    if (a4.resp_probe) out.push("Se recomandă probe ventilatorii");
    if (a4.resp_repaus) out.push(`Se recomandă repaus, izolare și tratament la domiciliu pe o perioadă de ${a4.resp_repaus_zile || "___"} zile`);
  }
  if (a4.cardiovascular_status === "Modificat") {
    if (a4.cv_cardiologie_ped) out.push("Se recomandă consult de specialitate serviciu Cardiologie pediatrică");
    if (a4.cv_cardiologie) out.push("Se recomandă consult de specialitate Cardiologie");
    if (a4.cv_ecocardiografie) out.push("Se recomandă Ecocardiografie (Ecodoppler)");
    if (a4.cv_holter_ekg) out.push("Se recomandă Holter EKG (24 de ore)");
    if (a4.cv_holter_ta) out.push("Se recomandă Holter TA (24 de ore)");
    if (a4.cv_in_observatie) out.push("În observație (suflu sistolic) — reevaluare semestrială");
    // NOTĂ: `program_individualizat` (PIPA) NU este inclus aici — este afișat separat
    // în secțiunea "Antrenament" din Concluzii aviz medico-sportiv (cu bold).
  }
  if (a4.digestiv_status === "Modificat") {
    if (a4.dig_ecografie) out.push("Se recomandă Ecografie abdominală");
    if (a4.dig_gastro) out.push("Se recomandă control de specialitate Gastroenterologie");
  }
  if (a4.urogenital_status === "Modificat") {
    if (a4.uro_urologie) out.push("Se recomandă control de specialitate serviciu Urologie");
    if (a4.uro_nefrologie) out.push("Se recomandă control de specialitate serviciu Nefrologie");
  }
  if (a4.endocrin_status === "Modificat" && a4.endo_endocrinologie) out.push("Control de specialitate Endocrinologie");
  if (a4.acuitate_vizuala_status === "Modificat") out.push("Consult oftalmologic periodic");
  if (a4.sistem_nervos_status === "Modificat" && a4.sistem_nervos_afectiuni_preexistente)
    out.push("Se cere document medical prin care se atestă acordul medicului curant neurolog pentru sportul de performanță");
  if (a4.sistem_nervos_status === "Modificat" && a4.sistem_nervos_debut_recent) out.push("Consult neurologic");
  if (a4.cavitate_orala_status === "Modificat" && (a4.cavitate_carii_superficiale || a4.cavitate_carii_profunde))
    out.push("Consult și tratament stomatologic (pentru asanarea focarelor infecțioase)");
  if (a4.cavitate_orala_status === "Modificat" && (a4.cavitate_hipertrofie_stanga || a4.cavitate_hipertrofie_dreapta || a4.cavitate_hipertrofie_bilaterala))
    out.push("Consult ORL");
  // NOTĂ: `program_individualizat` (PIPA) NU este inclus aici — apare separat în secțiunea
  // "Antrenament" din Concluzii aviz medico-sportiv (cu bold). Este încă afișat inline
  // în Referat medical prin `collectAtelier4Recs` (fișier separat).
  return out;
};

/**
 * Text-uri standardizate pentru recomandarea de Nutriție în Concluzii aviz medico-sportiv,
 * alese automat pe baza IMC-ului din Atelier 1. Se aplică pentru toți sportivii, indiferent
 * de textul introdus manual în `a1.recomandare_nutritie` (care este oricum tratat separat
 * ca "Recomandare nutriție personalizată" dacă e completat).
 */
const NUTRITIE_UNIVERSAL = "Este INTERZISĂ folosirea băuturilor energizante precum și a substanțelor psihoactive, pentru că pot cauza alterarea stării de sănătate și a antrenamentelor sportive pe termen lung. De asemenea, se recomandă evitarea băuturilor carbogazoase cu zahăr adăugat, alimentelor ultra-procesate, prăjelilor și consumului de mese copioase cu mai puțin de 2-3 ore înainte de antrenament sau competiție. Pentru autoadministrarea suplimentelor alimentare și a unor medicamente este de preferat consultarea inițială a listelor cu substanțe interzise (ANAD, verifică portal.anad.gov.ro) pentru a evita efectele adverse precum și descalificarea din competițiile sportive.";

// Formatare LOCALĂ YYYY-MM-DD (evită shift-ul cu 1 zi cauzat de `toISOString()` în GMT+2/+3).
const toLocalIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};


const buildNutritieRec = (a1) => {
  const imc = parseFloat(String(a1?.imc || "").replace(",", "."));
  if (Number.isNaN(imc) || imc <= 0) return null;
  if (imc < 18.5) {
    return `Alimentație hipercalorică moderată, bogată în nutrienți de calitate, cu un aport proteic și glucidic optim repartizat în 5–6 mese și gustări zilnice. Se recomandă suplimentarea dietei cu gustări dense nutritiv (fructe uscate, nuci, oleaginoase, smoothie-uri) și monitorizarea curbei ponderale.`;
  }
  if (imc <= 24.9) {
    return `Alimentație normocalorică și normoprotidică, adecvată volumului și intensității antrenamentelor. Proteine de înaltă valoare biologică asociate cu grăsimi sănătoase (nesaturate) și carbohidrați complecși cu indice glicemic mediu/scăzut. Hidratare optimă pre-efort, în timpul și post-efort.`;
  }
  // imc >= 25
  return `Alimentație supravegheată, hipocalorică moderată și normo-protidică sprijinită (pentru protejarea masei musculare active). Reducerea aportului de glucide rafinate, zaharuri simple și grăsimi saturate/hidrogenate. Accent pe legume proaspete, fibre și hidratare corespunzătoare (minimum 35–40 ml/kg corp).`;
};

/**
 * Agregă TOATE recomandările din întreaga fișă (Referat curent + Ateliere 1..5 curente
 * + istoricul complet: reviews[] cu recomandari + fise_complete[] snapshot-uri ale
 * ateliere/referat de la vize anterioare + AI analiza analizelor din Atelier 5).
 * Returnează un array de string-uri (fără duplicate goale).
 */
const collectAllRecomandari = (patient) => {
  const acc = [];
  const push = (label, val) => {
    if (nonEmpty(val)) acc.push(`${label ? label + ": " : ""}${String(val).trim()}`);
  };
  const pushRaw = (val) => {
    if (nonEmpty(val)) acc.push(String(val).trim());
  };
  // `skipStareSanatate` = când e true, sărim peste recomandările Atelier 1/3/4 pentru
  // că ele apar în secțiunea "Stare de sănătate" (afișată separat mai sus). Se folosește
  // pentru fișa curentă. Pentru snapshot-urile istorice (fise_complete[]) o lăsăm false,
  // să nu se piardă contextul temporal.
  const collectFromAteliere = (source, prefix = "", skipStareSanatate = false) => {
    const a1 = source.atelier_1 || {};
    if (!skipStareSanatate) {
      push(`${prefix}Recomandări posturale`, a1.recomandari_posturale);
    }
    push(`${prefix}Nutriție`, a1.recomandare_nutritie);
    // Recomandare nutriție bazată pe IMC + text universal (obligatoriu pentru toți sportivii).
    // Se adaugă DOAR pentru fișa curentă (prefix ""), nu se re-repetă pentru snapshot-uri istorice.
    if (prefix === "") {
      const nutritieRec = buildNutritieRec(a1);
      if (nutritieRec) acc.push(`Nutriție: ${nutritieRec}`);
      acc.push(`Nutriție (recomandare generală): ${NUTRITIE_UNIVERSAL}`);
    }
    // I.F.M.A — includem interpretarea (recomandarea clinică) în aviz (doar snapshot-uri istorice; pentru fișa curentă apare în "Stare de sănătate")
    if (!skipStareSanatate && nonEmpty(a1.i_fma) && nonEmpty(a1.i_fma_interpretare) && a1.i_fma_status !== "incomplete") {
      acc.push(`${prefix}I.F.M.A ${parseFloat(a1.i_fma).toFixed(2)} — ${a1.i_fma_interpretare.trim()}`);
    }
    if (!skipStareSanatate) pushRaw(a1.observatii);
    const a2 = source.atelier_2 || {};
    if (!a2.astrand_recomandare_dismissed) {
      push(`${prefix}Revenire post-Astrand`, a2.astrand_revenire_recomandare);
    }
    pushRaw(a2.observatii);
    const a3src = source.atelier_3 || {};
    if (!skipStareSanatate) {
      pushRaw(a3src.observatii);
      if (a3src.traseu_limite_normale && nonEmpty(a3src.traseu_limite_normale_av)) {
        acc.push(`${prefix}Traseu în limite normale cu AV: ${String(a3src.traseu_limite_normale_av).trim()}`);
      }
      if (a3src.revenire_ekg && nonEmpty(a3src.revenire_ekg_zile)) {
        acc.push(`${prefix}Revenire la EKG la ${String(a3src.revenire_ekg_zile).trim()} zile`);
      }
    }
    // Atelier 4 — TOATE recomandările generate dinamic din câmpurile bifate
    if (!skipStareSanatate) {
      collectAtelier4Recomandari(source).forEach((r) => acc.push(r));
      pushRaw((source.atelier_4 || {}).observatii);
    }
    const a5 = source.atelier_5 || {};
    pushRaw(a5.recomandari);
    pushRaw(a5.observatii);
    const referatRecs = (source.referat || {}).recomandari || [];
    referatRecs.forEach((r) => nonEmpty(r?.text) && acc.push(r.text.trim()));
  };

  // 1) Fișa curentă (Referat + Ateliere 1..5) — skip Atelier 1/3/4 (apar în „Stare de sănătate")
  collectFromAteliere(patient || {}, "", true);

  // 2) Reviews istorice (fiecare consultație medicală salvată)
  const reviews = (patient?.reviews || []);
  reviews.forEach((r) => {
    if (nonEmpty(r?.recomandari)) {
      const dateTag = r.date ? ` (${new Date(r.date).toLocaleDateString("ro-RO")})` : "";
      acc.push(`${r.recomandari.trim()}${dateTag}`);
    }
  });

  // 3) Fișe complete (snapshot-uri de la vize anterioare) — sortate cronologic ascendent
  const fise = (patient?.fise_complete || []).slice().sort((a, b) => {
    const da = a?.date ? new Date(a.date).getTime() : 0;
    const db = b?.date ? new Date(b.date).getTime() : 0;
    return da - db;
  });
  fise.forEach((fisa) => {
    const dateTag = fisa?.date ? ` (aviz nr. ${fisa.numar_viza || "?"} · ${new Date(fisa.date).toLocaleDateString("ro-RO")})` : "";
    // Colectăm dintr-un obiect proxy asemănător cu patient
    const before = acc.length;
    collectFromAteliere(fisa, "");
    // Adăugăm tag-ul temporal pe recomandările nou introduse (dacă nu îl au deja)
    for (let i = before; i < acc.length; i++) {
      if (!acc[i].includes("(aviz nr.")) acc[i] = `${acc[i]}${dateTag}`;
    }
  });

  // 4) AI analiza analizelor Atelier 5 (recomandările cache-uite pe fișier)
  // Nu avem acces la metadata GridFS din obiect, dar în viitor le putem expune prin API.
  // Momentan îl lăsăm pentru extensie ulterioară.

  // Deduplicare — dacă textul e identic (fără tag-uri), păstrăm doar prima apariție
  const seen = new Set();
  return acc.filter((s) => {
    // Normalizare fără tag-urile de dată pentru comparare
    const norm = s.replace(/\s*\((aviz nr\.|[0-9]{1,2}\.[0-9]{1,2}\.).*?\)\s*$/i, "").toLowerCase().replace(/\s+/g, " ").trim();
    if (seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
};

/**
 * Compune blocul narativ de „Diagnostic" din ateliere.
 *
 * Reguli (spec doctor):
 *  - Dacă Atelier 4 → „Normal clinic" bifat → doar „Sănătos clinic".
 *  - Altfel se adună (doar câmpurile completate/bifate):
 *    • Atelier 4 → toate „Descriere modificare" per sistem (dacă sistemul e „Modificat")
 *      + textul liber „țesut" (dacă există) + „Observații suplimentare".
 *    • Atelier 4 → „În observație (suflu sistolic) — reevaluare semestrială" (bifat).
 *    • Atelier 4 → „Discromat" (vedere cromatică).
 *    • Atelier 4 → „Necesită corecție optică" (dacă bifat).
 *    • Atelier 4 → checkbox-urile bifate din „Examenul cavității orale" (când e „Modificat").
 *    • Atelier 1 → item-urile din „Evaluare Posturală" marcate (Da / sub-checkbox-uri)
 *      + „Observații Suplimentare".
 *    • Atelier 5 → „Concluzii".
 */
const buildDiagnosticNarativ = (patient) => {
  const a1 = patient?.atelier_1 || {};
  const a4 = patient?.atelier_4 || {};
  const a5 = patient?.atelier_5 || {};

  // „Asimptomatic" apare mereu ca prima linie când e bifat.
  const prefix = a4.asimptomatic ? ["Asimptomatic"] : [];

  // Scurtcircuit dacă medicul a bifat „Normal clinic".
  if (a4.normal_clinic) return [...prefix, "Sănătos clinic"].join("\n");

  const parts = [...prefix];

  // Atelier 4 → Descriere modificare per sistem (doar când e „Modificat")
  const modifBlocks = [
    ["tegumente_status", "tegumente_descriere"],
    ["ganglionar_status", "ganglionar_descriere"],
    ["locomotor_status", "locomotor_descriere"],
    ["respirator_status", "respirator_descriere"],
    ["cardiovascular_status", "cardiovascular_descriere"],
    ["digestiv_status", "digestiv_descriere"],
    ["urogenital_status", "urogenital_descriere"],
    ["endocrin_status", "endocrin_descriere"],
  ];
  modifBlocks.forEach(([statusKey, descKey]) => {
    if (a4[statusKey] === "Modificat" && nonEmpty(a4[descKey])) {
      parts.push(a4[descKey].trim());
    }
  });
  // Țesut adipos — text liber (nu are toggle Normal/Modificat).
  // Excludem cazul „normoponderal" (auto-populat cu „Țesut adipos normal reprezentat"),
  // care nu reprezintă un element de diagnostic.
  if (
    nonEmpty(a4.tesut_descriere) &&
    a4.tesut_status !== "normoponderal" &&
    a4.tesut_descriere.trim() !== "Țesut adipos normal reprezentat"
  ) {
    parts.push(a4.tesut_descriere.trim());
  }

  // Atelier 4 → cv „În observație (suflu sistolic)"
  if (a4.cv_in_observatie) {
    parts.push("În observație (suflu sistolic) — reevaluare semestrială");
  }

  // Atelier 4 → Discromat
  if (a4.vedere_cromatica_status === "Discromat") parts.push("Discromat");

  // Atelier 4 → Necesită corecție optică
  if (
    a4.acuitate_vizuala_status === "Modificat" &&
    a4.acuitate_vizuala_optie === "necesita_corectie"
  ) {
    parts.push("Necesită corecție optică");
  }

  // Atelier 4 → Examenul cavității orale (checkbox-uri bifate, doar când e „Modificat")
  if (a4.cavitate_orala_status === "Modificat") {
    const cav = [];
    if (a4.cavitate_carii_superficiale) cav.push("carii superficiale");
    if (a4.cavitate_carii_profunde) cav.push("carii profunde");
    if (a4.cavitate_aparat_ortodontic) cav.push("aparat ortodontic");
    if (a4.cavitate_eritem_faringe) cav.push("eritem faringe");
    if (a4.cavitate_hipertrofie_stanga) cav.push("hipertrofie amigdaliană stângă");
    if (a4.cavitate_hipertrofie_dreapta) cav.push("hipertrofie amigdaliană dreaptă");
    if (a4.cavitate_hipertrofie_bilaterala) cav.push("hipertrofie amigdaliană bilaterală");
    if (cav.length) parts.push(`Examenul cavității orale: ${cav.join(", ")}`);
  }

  // Atelier 4 → Observații suplimentare (text liber)
  if (nonEmpty(a4.observatii)) parts.push(a4.observatii.trim());

  // Atelier 1 → Evaluare Posturală (item-uri Da + sub-checkbox-uri bifate)
  const post = [];
  if (a1.tulburari_statica === "Da") {
    const tulb = [];
    if (a1.tulb_atitudine_cifotica) tulb.push("atitudine cifotică");
    if (a1.tulb_cifoza) tulb.push("cifoză");
    if (a1.tulb_atitudine_scoliotica) tulb.push("atitudine scoliotică");
    if (a1.tulb_scolioza) tulb.push("scolioză");
    if (a1.tulb_atitudine_cifoscoliotica) tulb.push("atitudine cifoscoliotică");
    if (a1.tulb_cifoscolioza) tulb.push("cifoscolioză");
    if (a1.tulb_atitudine_hiperlordotica) tulb.push("atitudine hiperlordotică lombară");
    if (a1.tulb_hiperlordoza) tulb.push("hiperlordoză lombară compensatorie");
    if (tulb.length) post.push(`tulburări de statică vertebrală (${tulb.join(", ")})`);
    else post.push("tulburări de statică vertebrală");
  }
  if (a1.asimetrie_umeri === "Da") post.push("asimetrie umeri");
  if (a1.asimetrie_torace === "Da") post.push("asimetrie torace (anormal dezvoltate)");
  if (a1.asimetrie_membre_inf === "Da") {
    const fmtTip = (t) => (t === "Semnificativa" ? "semnificativă (>2cm)" : String(t || "").toLowerCase());
    const detalii = [];
    if (a1.asimetrie_membre_inf_stang) detalii.push(`stâng: ${fmtTip(a1.asimetrie_membre_inf_stang)}`);
    if (a1.asimetrie_membre_inf_drept) detalii.push(`drept: ${fmtTip(a1.asimetrie_membre_inf_drept)}`);
    // Legacy fallback dacă noile câmpuri per-membru sunt goale
    if (detalii.length === 0 && a1.asimetrie_membre_inf_tip) {
      detalii.push(fmtTip(a1.asimetrie_membre_inf_tip));
    }
    const suffix = detalii.length ? ` (${detalii.join("; ")})` : "";
    post.push(`asimetrie membre inferioare${suffix}`);
  }
  if (a1.genu_valgum === "Da") post.push("genu valgum");
  if (a1.genu_varum === "Da") post.push("genu varum");
  if (a1.picior_addus === "Da") {
    const g = a1.picior_addus_grad ? ` (${a1.picior_addus_grad})` : "";
    post.push(`picior addus${g}`);
  }
  if (post.length) parts.push(`Evaluare posturală: ${post.join("; ")}.`);

  // Atelier 1 → Observații Suplimentare
  if (nonEmpty(a1.observatii)) parts.push(a1.observatii.trim());

  // Atelier 5 → Sindroame biologice bifate (apar ca diagnostic independent de sub-checkboxes)
  const sindroameLabels = [];
  if (a5.sindrom_citoliza_hepatica) sindroameLabels.push("Sindrom de citoliza hepatica (TGO/TGP crescute)");
  if (a5.sindrom_inflamator_biologic) sindroameLabels.push("Sindrom inflamator biologic (VSH crescut)");
  if (a5.sindrom_rhabdomioliza) sindroameLabels.push("Sindrom de rhabdomioliza de suprasolicitare (hiperCKemie izolată de efort)");
  if (a5.sindrom_anemic) sindroameLabels.push("Sindrom anemic / suspiciune beta-talasemie minoră");
  if (sindroameLabels.length) parts.push(...sindroameLabels);

  // Atelier 5 → Concluzii
  if (nonEmpty(a5.concluzii)) parts.push(a5.concluzii.trim());

  return parts.join("\n");
};

// Recomandare generală de antrenamente (întotdeauna adăugată în aviz)
const RECOMANDARE_ANTRENAMENTE_GENERAL =
  "Antrenamente cu dozare gradată, conform etapei de pregătire și toleranței individuale cu accent initial pe pregătire fizică generală (pentru imbunatatirea capacitatii aerobe de efort) și pregătire specifica (imbunatatirea fortei explozive/tehnica, imbunatatirea calitatilor psihomotorii), cu încălzire atentă.";

// Recomandare generală de refacere — fallback când nu putem determina vârsta.
const RECOMANDARE_REFACERE_GENERAL = "Refacere complexă post-efort.";

// Recomandare de refacere bazată pe vârstă (afișată în secțiunea "Recomandări" din Concluzii)
const buildRefacereRec = (patient) => {
  const dob = patient?.date_personale?.data_nasterii;
  if (!dob) return RECOMANDARE_REFACERE_GENERAL;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return RECOMANDARE_REFACERE_GENERAL;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  if (age >= 3 && age < 10) {
    return "Refacere predominant naturală: asigurarea unui somn de noapte adecvat vârstei (9-11 ore — cu ora de culcare 22/22:30), hidratare optimă și alimentație echilibrată. Nu sunt necesare mijloace farmacologice sau proceduri fizice speciale. Recreere activă prin joc liber.";
  }
  if (age >= 10 && age < 18) {
    return "Refacere mixtă (naturală și fizică): somn odihnitor (8-10 ore — cu ora de culcare 22/22:30), reechilibrare hidro-electrolitică și acido-bazică zilnică post-efort (rehidratare cu băuturi izotonice ce conțin sodiu, glucoză, potasiu, magneziu într-o rată de 1,25-1,5 litri la un litru de apă pierdut, în 2-4 ore imediat post-efort — ex. Litorsal, Gatorade, etc.). Se recomandă stretching ghidat, hidromasaj/dușuri decontractante, masaj ușor de relaxare și gimnastică posturală/de compensare. Se vor evita procedurile termice extreme sau suplimentarea nejustificată.";
  }
  if (age >= 18) {
    return "Refacere complexă și intensificată: reechilibrare hidro-electrolitică și acido-bazică zilnică post-efort (rehidratare cu băuturi izotonice ce conțin sodiu, glucoză, potasiu, magneziu într-o rată de 1,25-1,5 litri la un litru de apă pierdut, în 2-4 ore imediat post-efort — ex. Litorsal, Gatorade, etc.). Mijloace fizice și balneofizioterapeutice (hidroterapie, crioterapie/băi de gheață, masaj sportiv decontractant, compresie pneumatică). Suplimentare nutrițională/metabolică avizată și refacere psihologică.";
  }
  return RECOMANDARE_REFACERE_GENERAL;
};

// Recomandare intensificată de refacere (adăugată dacă Ruffier este slab)
const RECOMANDARE_REFACERE_INTENSIFICATA =
  "Refacere complexa intensificata: - neuromusculara (masaj, hidroterapie calda, bazin) - metabolica (reechilbrare hidroelectrolitica, alcalinizare zilnica post efort) - neuropsihica (activitati extrasportive - hobby): odihna activa si pasiva (somn minim 8h, incepand ideal cu 2 ore inainte de miezul noptii) - cel mult 2/3h de ecran pe zi.";

// Recomandări suplimentare condiționate de rezultatul Ruffier
const buildRuffierRecomandari = (patient) => {
  const a2 = patient?.atelier_2 || {};
  const interp = (a2.ruffier_interpretare || "").toLowerCase();
  const out = [];
  const isSlab =
    interp.includes("nesatisfăcător") ||
    interp.includes("nesatisfacator") ||
    interp.includes("adaptare foarte slabă") ||
    interp.includes("adaptare foarte slaba");
  // Dacă adminul a bifat X pe recomandarea Ruffier în Atelier 2, o suprimăm complet aici.
  if (!a2.ruffier_recomandare_dismissed) {
    if (interp.includes("nesatisfăcător") || interp.includes("nesatisfacator")) {
      out.push("Monitorizare puls (Dozare gradată a efortului la antrenamente fără suprasolicitare - vezi ghid). De refăcut proba Ruffier la 30 de zile de către antrenor și în cazul aceluiași rezultat (mai mare sau egal cu 10) revine pentru reevaluare.");
    }
    if (interp.includes("adaptare foarte slabă") || interp.includes("adaptare foarte slaba")) {
      out.push("Monitorizarea sportivului in timpul antrenamentului a tolerantei la efort si program individual de antrenament");
    }
    if (isSlab) {
      out.push(RECOMANDARE_REFACERE_INTENSIFICATA);
    }
  }
  return out;
};

// Recomandare bazată pe vârstă pentru secțiunea Antrenament din Concluzii aviz medico-sportiv.
// Praguri: 3-9 ani, 10-17 ani, 18+ ani (conform ghidului medico-sportiv).
const buildAntrenamentAgeRec = (patient) => {
  const dob = patient?.date_personale?.data_nasterii;
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  if (age >= 3 && age < 10) {
    return "Efort adaptat vârstei: activități bazate pe jocuri de mișcare pentru dezvoltarea calităților motrice de bază (coordonare, echilibru, agilitate, suplețe). Se vor evita eforturile anaerobe intense, sarcinile axiale crescute (purtare de greutăți, alergare pe suprafețe dure, aterizări din săritură, etc.) și specializarea timpurie pe post/probă.";
  }
  if (age >= 10 && age < 18) {
    return "Antrenament cu dozare gradată conform etapei de creștere și dezvoltare. Accent pe pregătirea fizică generală, dezvoltarea capacității aerobe și învățarea tehnicii corecte a mișcărilor. Se recomandă programe specifice de profilaxie posturală (întărirea musculaturii centurii abdominale și pelvine/paravertebrale) și evitarea supraîncărcării osteo-articulare în perioadele de puse de creștere.";
  }
  if (age >= 18) {
    return "Efort individualizat conform cerințelor de efort specifice probei/postului și toleranței individuale. Microcicluri cu alternanță optimă între efortul specific (aerob/anaerob) și perioadele de refacere/recuperare. Monitorizarea încărcării de antrenament și aplicarea măsurilor de protecție articulară/musculară.";
  }
  return null;
};

// Recomandări dedicate secțiunii "Antrenament" din Concluzii aviz medico-sportiv.
// Sunt afișate separat, cu bold, în ordinea: vârstă → PIPA → Ruffier → sindroame Atelier 5.
const buildAntrenamentRecs = (patient) => {
  const out = [];
  const ageRec = buildAntrenamentAgeRec(patient);
  if (ageRec) out.push(ageRec);
  const a2 = patient?.atelier_2 || {};
  const a4 = patient?.atelier_4 || {};
  if (a2.pipa || a4.program_individualizat) {
    out.push("Program individualizat de pregătire și antrenament (PIPA)");
  }
  if (a2.monitorizare_puls) {
    out.push("Monitorizare puls");
  }
  const ruffier = buildRuffierRecomandari(patient);
  out.push(...ruffier);
  // Atelier 1 — sub-recomandarea „Activitate fizică adaptată..." dacă asimetrie_torace = Da și bifată
  const a1 = patient?.atelier_1 || {};
  if (a1.asimetrie_torace === "Da" && a1.at_activitate_adaptata) {
    out.push("Activitate fizică adaptată cu accent pe exerciții pe postură, se evită supraîncărcările articulare extreme până la normalizarea nivelului vitaminei D");
  }
  // Atelier 1 — Exerciții de creștere a mobilității când mobilitatea e Medie sau Slabă
  if (a1.mobilitate === "Medie" || a1.mobilitate === "Slaba") {
    out.push("Exerciții de creștere a mobilității");
  }
  // Sub-recomandările din Atelier 5 destinate „Antrenament" (bold)
  const a5 = patient?.atelier_5 || {};
  if (a5.sindrom_citoliza_hepatica && a5.sch_deconditionare_temporara) {
    out.push("Decondiționare temporară de efort intens/susținut (reducerea încărcării)");
  }
  if (a5.sindrom_inflamator_biologic && a5.sib_efort_adaptat) {
    out.push("Efort adaptat (evitarea eforturilor maximale până la identificarea etiologică)");
  }
  if (a5.sindrom_rhabdomioliza && a5.sr_deconditionare_totala) {
    out.push("Decondiționare totală de efort (3-5 zile), urmată de reintroducerea treptată a efortului aerobic, hidratare orală susținută 3-4 ore pe zi (protecție renală); reînceperea activității susținute (conform programului de antrenament) se va face numai după normalizarea CK-ului");
  }
  if (a5.sindrom_anemic && a5.sa_limitare_eforturi) {
    out.push("Limitarea eforturilor de mare intensitate, anaerobe, până la corectarea anemiei");
  }
  return out;
};

// Recomandări dedicate secțiunii "Stare de sănătate" din Concluzii aviz medico-sportiv.
// Include recomandările din Ateliere 1 (posturale, IFMA, observații — nu Recomandare sport
// care a fost eliminată din Concluzii), 3 (traseu EKG, revenire EKG, observații) și 4
// (recomandările per sistem generate de checkbox-uri; fără PIPA care apare în „Antrenament").
const nonEmptyText = (v) => v !== undefined && v !== null && String(v).trim() !== "";
const buildStareSanatateRecs = (patient) => {
  const out = [];
  const a1 = patient?.atelier_1 || {};
  const a3 = patient?.atelier_3 || {};
  const a4 = patient?.atelier_4 || {};
  // Atelier 1
  if (nonEmptyText(a1.recomandari_posturale)) out.push(`Recomandări posturale: ${a1.recomandari_posturale.trim()}`);
  if (nonEmptyText(a1.i_fma) && nonEmptyText(a1.i_fma_interpretare) && a1.i_fma_status !== "incomplete") {
    out.push(`I.F.M.A ${parseFloat(a1.i_fma).toFixed(2)} — ${a1.i_fma_interpretare.trim()}`);
  }
  // Atelier 1 — sub-recomandarea „Dozarea 25-OH-Vitamine D..." apare dacă asimetrie_torace = „Da" și e bifată
  if (a1.asimetrie_torace === "Da" && a1.at_dozare_vit_d) {
    out.push("Dozarea 25-OH-Vitamine D serice. În caz de deficit/insuficiență, inițierea substituției cu Vitamina D3 (conform ghidurilor în vigoare) și dispensarizare prin Medicul de Familie/Pediatru. Program KT pentru corectarea/compensarea deficiențelor posturale secundare");
  }
  // Atelier 3 — „Traseu în limite normale" și „Observații" au fost eliminate (nu apar în Recomandări → Stare de sănătate)
  if (a3.revenire_ekg && nonEmptyText(a3.revenire_ekg_zile)) {
    out.push(`Revenire la EKG la ${String(a3.revenire_ekg_zile).trim()} zile`);
  }
  // Atelier 4 (via helper existent — PIPA e deja exclus). „Observații" Atelier 4 eliminate.
  out.push(...collectAtelier4Recomandari(a4));
  // Atelier 5 — sindroame biologice: sub-recomandările destinate „Stare de sănătate"
  const a5 = patient?.atelier_5 || {};
  // 1) Sindrom de citoliza hepatica
  if (a5.sindrom_citoliza_hepatica) {
    if (a5.sch_in_observatie) out.push("În observație (Sindrom de citoliza hepatica — TGO/TGP crescute)");
    if (a5.sch_control_transaminaze) out.push("Control transaminaze peste 15 zile");
    if (a5.sch_investigatii_suplimentare) out.push("Necesită investigații suplimentare");
  }
  // 2) Sindrom inflamator biologic
  if (a5.sindrom_inflamator_biologic) {
    if (a5.sib_dispensarizare_mf) out.push("Dispensarizare prin medicul de familie (pentru investigații suplimentare și tratament)");
    if (a5.sib_in_observatie) out.push("În observație (Sindrom inflamator biologic — VSH crescut)");
  }
  // 3) Sindrom de rhabdomioliza
  if (a5.sindrom_rhabdomioliza) {
    if (a5.sr_repeta_ck) out.push("Repetă dozare CK la 15 zile");
    if (a5.sr_in_observatie) out.push("În observație (Sindrom de rhabdomioliza de suprasolicitare)");
  }
  // 4) Sindrom anemic
  if (a5.sindrom_anemic) {
    if (a5.sa_in_observatie) out.push("În observație (Sindrom anemic / suspiciune beta-talasemie minoră)");
    if (a5.sa_dispensarizare_mf) out.push("Dispensarizare prin medicul de familie (pentru investigații suplimentare, inițierea tratamentului)");
  }
  return out;
};

// Recomandări de „Nutriție" pentru secțiunea Recomandări din Concluzii aviz medico-sportiv.
// Include: recomandare manuală (a1.recomandare_nutritie), recomandare bazată pe IMC și
// textul universal (obligatoriu pentru toți sportivii). Se elimină duplicatele.
const buildNutritieItems = (patient) => {
  const a1 = patient?.atelier_1 || {};
  const out = [];
  if (nonEmptyText(a1.recomandare_nutritie)) out.push(a1.recomandare_nutritie.trim());
  const imcBased = buildNutritieRec(a1);
  if (imcBased) out.push(imcBased);
  // Sub-recomandarea „Optimizarea raportului alimentar de calciu" apare dacă asimetrie_torace = Da și bifată
  if (a1.asimetrie_torace === "Da" && a1.at_optimizare_calciu) {
    out.push("Optimizarea raportului alimentar de calciu");
  }
  out.push(NUTRITIE_UNIVERSAL);
  const seen = new Set();
  return out.filter((s) => {
    const n = String(s).toLowerCase().trim();
    if (seen.has(n)) return false;
    seen.add(n);
    return true;
  });
};

// Toate recomandările „Antrenament" pentru Concluzii — vârstă + PIPA + Ruffier
// (via buildAntrenamentRecs) + recomandarea de refacere bazată pe vârstă.
// „RECOMANDARE_ANTRENAMENTE_GENERAL" a fost eliminată la cerere.
const buildAllAntrenamentItems = (patient) => {
  const out = [...buildAntrenamentRecs(patient)];
  const refacere = buildRefacereRec(patient);
  if (refacere) out.push(refacere);
  const seen = new Set();
  return out.filter((s) => {
    if (!nonEmptyText(s)) return false;
    const n = String(s).toLowerCase().trim();
    if (seen.has(n)) return false;
    seen.add(n);
    return true;
  });
};

// Restul recomandărilor („Altele") — filtrează din agregatul general tot ce e deja acoperit
// de Nutriție/Stare de sănătate/Antrenament, ca să nu se dubleze.
const buildOtherRecs = (patient, alreadyCovered) => {
  const all = collectAllRecomandari(patient);
  const covered = new Set(alreadyCovered.map((s) => String(s).toLowerCase().replace(/\s+/g, " ").trim()));
  return all.filter((s) => {
    const norm = String(s).toLowerCase().replace(/\s*\((aviz nr\.|[0-9]{1,2}\.[0-9]{1,2}\.).*?\)\s*$/i, "").replace(/\s+/g, " ").trim();
    // Filtrăm și prefix „Nutriție:" fiindcă apare deja în grup Nutriție
    if (norm.startsWith("nutriție:") || norm.startsWith("nutritie:") || norm.startsWith("nutriție (") || norm.startsWith("nutritie (")) return false;
    return !covered.has(norm);
  });
};

export const ConcluziiAvizButton = ({ patient, triggerVariant = "outline", className = "", initialDataCircuit = "", onDataCircuitChange, onUpdate }) => {
  const [open, setOpen] = useState(false);

  const dp = patient?.date_personale || {};
  const is = patient?.info_sportive || {};
  const a1r = patient?.atelier_1 || {};

  // Câmpuri editabile (default-uri auto-calculate)
  const [postSportiv, setPostSportiv] = useState("");
  const [numarFisa, setNumarFisa] = useState("");
  const [dataCircuit, setDataCircuit] = useState("");
  const [dataValabilDe, setDataValabilDe] = useState("");
  const [dataValabilPana, setDataValabilPana] = useState("");
  const [diagnostic, setDiagnostic] = useState("");
  const diagnosticRef = useRef(null);

  // Auto-grow textarea Diagnostic (fără scroll): setează height = scrollHeight.
  useEffect(() => {
    const el = diagnosticRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [diagnostic, open]);
  const [selectieText, setSelectieText] = useState("");
  const [recomandariText, setRecomandariText] = useState("");
  const [directorText, setDirectorText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [whoGrowth, setWhoGrowth] = useState(null);
  const [whoLoading, setWhoLoading] = useState(false);

  const numarViza = patient?.numar_viza || patient?.numar_ordine || "";
  const nume = `${(dp.nume || "").toUpperCase()} ${(dp.prenume || "").toUpperCase()}`.trim();
  const sport = is.sport_practicat || "";

  const recomandariAggregate = useMemo(() => collectAllRecomandari(patient), [patient]);
  const diagnosticAggregate = useMemo(() => buildDiagnosticNarativ(patient), [patient]);
  const antrenamentRecs = useMemo(() => buildAntrenamentRecs(patient), [patient]);
  const stareSanatateRecs = useMemo(() => buildStareSanatateRecs(patient), [patient]);
  const nutritieItems = useMemo(() => buildNutritieItems(patient), [patient]);
  const allAntrenamentItems = useMemo(() => buildAllAntrenamentItems(patient), [patient]);
  const otherRecs = useMemo(
    () => buildOtherRecs(patient, [...nutritieItems, ...stareSanatateRecs, ...allAntrenamentItems]),
    [patient, nutritieItems, stareSanatateRecs, allAntrenamentItems]
  );

  useEffect(() => {
    if (!open) return;
    // Re-inițializează valorile default la fiecare deschidere.
    // Post sportiv se pre-completează automat din info_sportive.post_categorie (editabil).
    const postDefault = (is?.post_categorie || "").trim();
    setPostSportiv(postDefault);
    // „Număr fișă" — sursă primară: override manual din Referat medical (`referat.fisa_nr`);
    // fallback pe `numar_viza`/`numar_ordine` din pacient. Sincronizare automată între Referat și Concluzii.
    const fisaNrOverride = String(patient?.referat?.fisa_nr || "").trim();
    setNumarFisa(fisaNrOverride || String(numarViza || ""));
    const today = new Date();
    const iso = toLocalIso(today);
    // Preferă data primită de la Referat medical (păstrează sincronizarea între cele două)
    const initialIso = initialDataCircuit && /^\d{4}-\d{2}-\d{2}$/.test(initialDataCircuit) ? initialDataCircuit : iso;
    setDataCircuit(initialIso);
    setDataValabilDe(initialIso);
    // Dacă în Referat medical s-a completat "Viza valabilă X zile/luni", folosim asta;
    // altfel, fallback pe valabilitatea implicită de 6 luni.
    const ref = patient?.referat || {};
    const val = Number(ref.viza_valabila_zile || 0);
    const unit = ref.viza_valabila_unit === "luni" ? "luni" : "zile";
    const base = initialIso ? new Date(initialIso + "T00:00:00") : today;
    if (val > 0) {
      const pana = new Date(base);
      if (unit === "luni") pana.setMonth(pana.getMonth() + val);
      else pana.setDate(pana.getDate() + val);
      setDataValabilPana(toLocalIso(pana));
    } else {
      setDataValabilPana(toLocalIso(addMonths(base, 6)));
    }
    setDiagnostic(diagnosticAggregate || "");
    setSelectieText(sport ? `Apt ${sport}${postDefault ? ` (${postDefault})` : ""} - biotip perfectibil morfo-funcțional.` : "");
    // Recomandările — listă numerotată. La final adăugăm recomandările generale de antrenament
    // și refacere. Ruffier + PIPA + vârstă sunt afișate separat în secțiunea "Antrenament"
    // cu bold (nu se duplică în această listă).
    const trainingRecs = [
      RECOMANDARE_ANTRENAMENTE_GENERAL,
      buildRefacereRec(patient),
    ];
    const allRecs = [...recomandariAggregate, ...trainingRecs];
    // Deduplicare finală (case-insensitive, ignoră tag-urile temporale)
    const seenFinal = new Set();
    const uniqueRecs = allRecs.filter((s) => {
      const norm = s.replace(/\s*\((aviz nr\.|[0-9]{1,2}\.[0-9]{1,2}\.).*?\)\s*$/i, "").toLowerCase().replace(/\s+/g, " ").trim();
      if (seenFinal.has(norm)) return false;
      seenFinal.add(norm);
      return true;
    });
    if (uniqueRecs.length) {
      setRecomandariText(uniqueRecs.map((r, i) => `${i + 1}. ${r}`).join("\n"));
    } else {
      setRecomandariText("");
    }
    setDirectorText("");
    // Fetch WHO growth (aplicabil doar dacă pacient e între 5-19 ani)
    setWhoLoading(true);
    setWhoGrowth(null);
    (async () => {
      try {
        const r = await patientApi.whoGrowth(patient.id);
        setWhoGrowth(r);
      } catch (e) {
        // Silent fail — nu blocăm dialogul
        console.warn("WHO growth fetch:", e?.response?.data?.detail || e?.message);
      } finally {
        setWhoLoading(false);
      }
    })();
  }, [open, numarViza, sport, diagnosticAggregate.length, recomandariAggregate.length, is?.post_categorie, patient?.referat?.viza_valabila_zile, patient?.referat?.viza_valabila_unit, patient?.referat?.fisa_nr, initialDataCircuit]);

  const handleAiGenerate = async () => {
    setAiLoading(true);
    toast.info("AI generează avizul... (10-30s)");
    try {
      const r = await patientApi.aiGenerateAviz(patient.id, postSportiv);
      if (r.diagnostic) setDiagnostic(r.diagnostic);
      if (r.selectie) setSelectieText(r.selectie);
      if (r.recomandari) setRecomandariText(r.recomandari);
      toast.success("Aviz generat cu AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare AI");
    } finally {
      setAiLoading(false);
    }
  };

  // Update selecție când se schimbă postul (dar nu suprascrie dacă user a editat manual)
  useEffect(() => {
    if (!open) return;
    setSelectieText(
      sport
        ? `Apt ${sport}${postSportiv ? ` (${postSportiv})` : ""} - biotip perfectibil morfo-funcțional.`
        : ""
    );
  }, [open, postSportiv, sport]);

  const handlePrint = () => {
    const printArea = document.getElementById("concluzii-aviz-print-area");
    if (!printArea) return;
    // Snapshot HTML pentru istoric — se salvează chiar și dacă utilizatorul nu printează (best-effort)
    (async () => {
      try {
        const snapUpdated = await patientApi.createConcluziiSnapshot(
          patient.id,
          printArea.outerHTML,
          patient.numar_viza || null
        );
        if (onUpdate) onUpdate(snapUpdated);
      } catch (err) {
        console.warn("Snapshot concluzii failed:", err);
      }
    })();
    const styles = Array.from(document.querySelectorAll("style,link[rel=stylesheet]"))
      .map((s) => s.outerHTML)
      .join("\n");
    const html = `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="utf-8" />
  <title>Concluzii aviz medico-sportiv - ${nume}</title>
  ${styles}
  <style>
    @page { size: A4; margin: 14mm; }
    * { box-sizing: border-box; }
    body { background: white; color: #111; font-family: 'Times New Roman', Georgia, serif; padding: 0; margin: 0; font-size: 12pt; line-height: 1.35; }
    .aviz-header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 12px; }
    .aviz-header h1 { font-size: 14pt; font-weight: bold; margin: 0; letter-spacing: 0.5px; text-transform: uppercase; }
    .aviz-header h2 { font-size: 13pt; font-weight: bold; margin: 4px 0 0 0; text-transform: uppercase; letter-spacing: 0.5px; }
    .aviz-header p { font-size: 10pt; margin: 4px 0 0 0; font-style: italic; }
    .kv { display: grid; grid-template-columns: max-content 1fr; column-gap: 10px; row-gap: 4px; margin-bottom: 10px; }
    .kv .k { font-weight: bold; }
    .aviz-section { margin: 10px 0; }
    .aviz-section h3 { font-size: 12pt; font-weight: bold; text-transform: uppercase; margin: 0 0 4px 0; letter-spacing: 0.5px; }
    .aviz-block { white-space: pre-line; text-align: justify; }
    .aviz-apt { text-align: center; font-weight: bold; font-size: 13pt; margin: 12px 0; text-transform: uppercase; letter-spacing: 1px; }
    .aviz-valid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px; }
    .aviz-footer { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .aviz-footer .col { text-align: center; }
    .aviz-footer .line { border-top: 1px solid #111; margin: 40px 20px 4px; }
    .aviz-disclaimer { margin-top: 20px; font-size: 9pt; font-style: italic; text-align: center; border-top: 1px dashed #999; padding-top: 6px; }
    button { display: none !important; }
    input, textarea { border: none !important; background: transparent !important; padding: 0 !important; resize: none !important; overflow: hidden !important; }
  </style>
</head>
<body>${printArea.innerHTML}</body>
</html>`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) {
      toast.error("Pop-up blocat — permite ferestrele pop-up pentru această pagină.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => {
      setTimeout(() => { w.focus(); w.print(); }, 400);
    };
  };

  const nrFisaLabel = numarFisa ? `nr. ${numarFisa}/${fmt(dataCircuit)}` : `${fmt(dataCircuit)}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={triggerVariant}
          className={`gap-2 border-primary/40 hover:bg-primary/5 ${className}`}
          data-testid="concluzii-aviz-btn"
        >
          <FileCheck2 className="w-4 h-4" />
          Concluzii aviz medico-sportiv
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="concluzii-aviz-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-primary" />
              Concluzii aviz medico-sportiv
            </DialogTitle>
            <DialogDescription className="sr-only">
              Document editabil pentru concluziile avizului medico-sportiv al pacientului. Câmpurile sunt pre-completate din ateliere și pot fi modificate înainte de imprimare / export PDF.
            </DialogDescription>
            <div className="flex gap-2 mr-8">
              <Button
                size="sm"
                variant="default"
                onClick={handleAiGenerate}
                disabled={aiLoading}
                className="gap-1 bg-violet-600 hover:bg-violet-700"
                data-testid="concluzii-aviz-ai-btn"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Completează cu AI
              </Button>
              <Button size="sm" onClick={handlePrint} data-testid="concluzii-aviz-print" className="gap-1">
                <Printer className="w-4 h-4" /> Printează / PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="gap-1">
                <X className="w-4 h-4" /> Închide
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Câmpuri editabile deasupra */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 border rounded-md p-3 bg-slate-50/50 mb-4 print:hidden">
          <div>
            <Label className="text-xs">Post sportiv (ex: atacant)</Label>
            <Input
              value={postSportiv}
              onChange={(e) => setPostSportiv(e.target.value)}
              placeholder="opțional"
              data-testid="concluzii-aviz-post"
            />
          </div>
          <div>
            <Label className="text-xs">Nr. fișă / viză</Label>
            <Input value={numarFisa} onChange={(e) => setNumarFisa(e.target.value)} data-testid="concluzii-aviz-nrfisa" />
          </div>
          <div>
            <Label className="text-xs">Data circuit</Label>
            <Input
              type="date"
              value={dataCircuit}
              onChange={(e) => {
                const nou = e.target.value;
                setDataCircuit(nou);
                // Sincronizează automat "Valabil de la" cu "Data circuit".
                setDataValabilDe(nou);
                // Dacă avem "Viza valabilă X zile/luni" din Referat, recompunem automat "Valabil până la"
                const ref = patient?.referat || {};
                const val = Number(ref.viza_valabila_zile || 0);
                const unit = ref.viza_valabila_unit === "luni" ? "luni" : "zile";
                if (nou && val > 0) {
                  const d = new Date(nou + "T00:00:00");
                  if (!Number.isNaN(d.getTime())) {
                    if (unit === "luni") d.setMonth(d.getMonth() + val);
                    else d.setDate(d.getDate() + val);
                    setDataValabilPana(toLocalIso(d));
                  }
                }
                // Propagă schimbarea către Referat medical (persistat imediat).
                if (typeof onDataCircuitChange === "function") onDataCircuitChange(nou);
              }}
              data-testid="concluzii-aviz-datacircuit"
            />
          </div>
          <div>
            <Label className="text-xs">Valabil până la</Label>
            <Input type="date" value={dataValabilPana} onChange={(e) => setDataValabilPana(e.target.value)} data-testid="concluzii-aviz-valabilpana" />
          </div>
        </div>

        {/* Zona printabilă (visible în preview + printată la Print) */}
        <div id="concluzii-aviz-print-area" className="bg-white text-black p-6 rounded-md border" style={{ fontFamily: "'Times New Roman', Georgia, serif" }}>
          <div className="aviz-header text-center border-b-2 border-black pb-2 mb-3">
            <h1 className="text-lg font-bold uppercase tracking-wide m-0">Cabinet medicină sportivă</h1>
            <h2 className="text-base font-bold uppercase tracking-wide mt-1">Concluzii aviz medico-sportiv</h2>
            <p className="text-xs italic mt-1">(întocmit în conformitate cu avizul medico-sportiv {nrFisaLabel})</p>
          </div>

          <div className="kv grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 mb-3 text-sm">
            <span className="k font-bold">Nume sportiv:</span><span data-testid="aviz-nume">{nume || "—"}</span>
            <span className="k font-bold">Sportul practicat:</span>
            <span data-testid="aviz-sport">
              {sport}{postSportiv ? ` - ${postSportiv}` : ""}
            </span>
            <span className="k font-bold">Număr fișă:</span><span>{numarFisa || "—"}</span>
            <span className="k font-bold">Data circuitului:</span><span>{fmt(dataCircuit)}</span>
          </div>

          <div className="aviz-section mt-3">
            <h3 className="text-sm font-bold uppercase tracking-wide mb-1">Diagnostic</h3>

            {/* Dezvoltare fizică — Talie / Greutate / IMC din Atelier 1 */}
            {(() => {
              const talieRaw = a1r.talie;
              const talieNum = parseFloat(String(talieRaw || "").replace(",", "."));
              let talieM = null;
              if (!Number.isNaN(talieNum) && talieNum > 0) {
                talieM = talieNum >= 3 ? (talieNum / 100).toFixed(2) : talieNum.toFixed(2);
              }
              const items = [];
              if (talieM) items.push(<><strong>Talie:</strong> {talieM} m</>);
              if (a1r.greutate) items.push(<><strong>Greutate:</strong> {a1r.greutate} kg</>);
              if (a1r.imc) items.push(<><strong>IMC:</strong> {a1r.imc}</>);
              if (items.length === 0) return null;
              return (
                <div className="mb-2" data-testid="aviz-diag-dezvoltare-fizica">
                  <h4 className="text-sm font-semibold text-slate-800 mb-1">Dezvoltare fizică</h4>
                  <div className="text-sm font-serif flex flex-wrap gap-x-4 gap-y-1 pl-2">
                    {items.map((el, i) => (
                      <span key={i}>{el}</span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Grafice OMS dezvoltare corporală (5-19 ani) — sub-secțiune cu font mai mic */}
            {whoLoading && (
              <div className="text-xs italic text-muted-foreground flex items-center gap-2 print:hidden mb-2 pl-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Calcul percentile WHO...
              </div>
            )}
            {whoGrowth?.applicable && (
              <div className="mb-2 who-growth-section" data-testid="who-growth-block">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 flex items-center gap-1 pl-2">
                  <Activity className="w-3 h-3 text-emerald-600 print:hidden" />
                  Grafice OMS dezvoltare corporală (5-19 ani)
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pl-2">
                  {whoGrowth.bmi && (
                    <div className="border rounded-md p-2 bg-slate-50/50" data-testid="who-bmi">
                      <p className="font-semibold text-[10px] uppercase text-muted-foreground">IMC-pentru-vârstă</p>
                      <p className="mt-1">
                        Valoare: <strong>{whoGrowth.bmi.value}</strong> kg/m²
                        &nbsp;·&nbsp; Percentila: <strong>{whoGrowth.bmi.percentile}</strong>
                        &nbsp;·&nbsp; z-score: <strong>{whoGrowth.bmi.z_score}</strong>
                      </p>
                      <p className="mt-1">
                        Interpretare:{" "}
                        <span className={
                          whoGrowth.bmi.interpretation === "normal"
                            ? "text-emerald-700 font-semibold"
                            : whoGrowth.bmi.interpretation.includes("subponderal")
                              ? "text-amber-700 font-semibold"
                              : "text-red-700 font-semibold"
                        }>
                          {whoGrowth.bmi.interpretation}
                        </span>
                        {whoGrowth.bmi.reference_range && (
                          <span className="text-[10px] text-muted-foreground"> · Referință: {whoGrowth.bmi.reference_range}</span>
                        )}
                      </p>
                    </div>
                  )}
                  {whoGrowth.height && (
                    <div className="border rounded-md p-2 bg-slate-50/50" data-testid="who-height">
                      <p className="font-semibold text-[10px] uppercase text-muted-foreground">Talie-pentru-vârstă</p>
                      <p className="mt-1">
                        Valoare: <strong>{whoGrowth.height.value}</strong> cm
                        &nbsp;·&nbsp; Percentila: <strong>{whoGrowth.height.percentile}</strong>
                        &nbsp;·&nbsp; z-score: <strong>{whoGrowth.height.z_score}</strong>
                      </p>
                      <p className="mt-1">
                        Interpretare:{" "}
                        <span className={
                          whoGrowth.height.interpretation === "normal"
                            ? "text-emerald-700 font-semibold"
                            : whoGrowth.height.interpretation.includes("scundă")
                              ? "text-amber-700 font-semibold"
                              : "text-blue-700 font-semibold"
                        }>
                          {whoGrowth.height.interpretation}
                        </span>
                        <span className="text-[10px] text-muted-foreground"> · Mediană WHO: {whoGrowth.height.reference_median} cm</span>
                      </p>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground italic mt-1 pl-2">
                  Vârsta: {whoGrowth.age_years} ani ({whoGrowth.age_months} luni) · Sex: {whoGrowth.sex} · Referință WHO Growth Reference 2007.
                </p>
              </div>
            )}
            {whoGrowth && whoGrowth.applicable === false && whoGrowth.age_years != null && (
              <div className="text-xs italic text-muted-foreground print:hidden mb-2 pl-2">
                ℹ Percentilele WHO se aplică doar între 5-19 ani (vârsta actuală: {whoGrowth.age_years} ani). {whoGrowth.reason}
              </div>
            )}

            {/* I.F.M.A + interpretare (din Atelier 1) — apare doar dacă e complet calculat */}
            {(() => {
              if (a1r.i_fma_status === "incomplete") return null;
              const ifmaNum = parseFloat(String(a1r.i_fma || "").replace(",", "."));
              if (!(!Number.isNaN(ifmaNum) && ifmaNum > 0)) return null;
              return (
                <div className="mb-2 text-sm font-serif pl-2" data-testid="aviz-diag-ifma">
                  <strong>I.F.M.A:</strong> {parseFloat(a1r.i_fma).toFixed(2)}
                  {a1r.i_fma_interpretare ? ` — ${a1r.i_fma_interpretare}` : ""}
                </div>
              );
            })()}

            {/* I.F.G (din Atelier 1) — dacă există */}
            {(() => {
              const ifgNum = parseFloat(String(a1r.i_fg || "").replace(",", "."));
              if (!(!Number.isNaN(ifgNum) && ifgNum > 0)) return null;
              return (
                <div className="mb-2 text-sm font-serif pl-2" data-testid="aviz-diag-ifg">
                  <strong>I.F.G:</strong> {parseFloat(a1r.i_fg).toFixed(2)}
                </div>
              );
            })()}

            <div className="aviz-block">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">Stare de sănătate</h4>
              <Textarea
                ref={diagnosticRef}
                value={diagnostic}
                onChange={(e) => setDiagnostic(e.target.value)}
                className="w-full text-sm font-serif resize-none overflow-hidden"
                rows={1}
                data-testid="concluzii-aviz-diagnostic"
              />
            </div>
          </div>

          <div className="aviz-section mt-3">
            <h3 className="text-sm font-bold uppercase tracking-wide mb-1">Selecție</h3>
            <div className="aviz-block">
              <Textarea
                value={selectieText}
                onChange={(e) => setSelectieText(e.target.value)}
                className="w-full min-h-[50px] text-sm font-serif"
                data-testid="concluzii-aviz-selectie"
              />
            </div>
          </div>

          {(nutritieItems.length > 0 || stareSanatateRecs.length > 0 || allAntrenamentItems.length > 0 || otherRecs.length > 0) && (
            <div className="aviz-section mt-3" data-testid="concluzii-aviz-recomandari-grouped">
              <h3 className="text-lg font-bold uppercase tracking-wide mb-1">Recomandări</h3>
              <div className="text-sm font-serif space-y-3">
                {nutritieItems.length > 0 && (
                  <div data-testid="rec-group-nutritie">
                    <p className="font-bold">Nutriție:</p>
                    <ol className="list-decimal pl-6 space-y-1">
                      {nutritieItems.map((r, i) => (
                        <li key={`nutr-${i}`}>{r}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {stareSanatateRecs.length > 0 && (
                  <div data-testid="rec-group-stare-sanatate">
                    <p className="font-bold">Stare de sănătate:</p>
                    <ol className="list-decimal pl-6 space-y-1">
                      {stareSanatateRecs.map((r, i) => (
                        <li key={`ss-${i}`}>{r}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {allAntrenamentItems.length > 0 && (
                  <div data-testid="rec-group-antrenament">
                    <p className="font-bold">Antrenament:</p>
                    <ol className="list-decimal pl-6 space-y-1">
                      {allAntrenamentItems.map((r, i) => (
                        <li key={`ant-${i}`} className="font-bold">{r}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {otherRecs.length > 0 && (
                  <div data-testid="rec-group-altele">
                    <p className="font-bold">Altele:</p>
                    <ol className="list-decimal pl-6 space-y-1">
                      {otherRecs.map((r, i) => (
                        <li key={`oth-${i}`}>{r}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Verdict final — preluat din Referat medical (patient.referat.concluzie).
              Dacă nu este selectat, afișăm implicit „Apt antrenamente și competiții". */}
          {(() => {
            const VERDICT_LABELS = {
              APT: "APT ANTRENAMENTE ȘI COMPETIȚII",
              APT_SUPRAVEGHERE: "APT ANTRENAMENTE ȘI COMPETIȚII SUB SUPRAVEGHERE MEDICALĂ",
              APT_INDIVIDUALIZAT: "APT ANTRENAMENTE INDIVIDUALIZATE SUB SUPRAVEGHERE",
              INAPT_TEMPORAR: "INAPT TEMPORAR",
            };
            const key = patient?.referat?.concluzie || "APT";
            const label = VERDICT_LABELS[key] || "APT ANTRENAMENTE ȘI COMPETIȚII";
            return (
              <div className="aviz-apt text-center font-bold text-base my-3 uppercase tracking-widest" data-testid="concluzii-aviz-verdict">
                {label}
              </div>
            );
          })()}

          <div className="aviz-valid grid grid-cols-2 gap-3 text-sm mt-2">
            <div><strong>Valabil de la:</strong> {fmt(dataValabilDe)}</div>
            <div><strong>Până la:</strong> {fmt(dataValabilPana)}</div>
          </div>

          <div className="aviz-footer grid grid-cols-2 gap-5 mt-10">
            <div className="col text-center">
              <strong>MEDIC</strong>
              <Textarea
                value={directorText}
                onChange={(e) => setDirectorText(e.target.value)}
                placeholder="Nume medic..."
                className="w-full min-h-[40px] text-sm font-serif text-center"
                data-testid="concluzii-aviz-director"
              />
              <div className="line border-t border-black mt-10 mx-5" />
              <div className="text-xs mt-1">(semnătură / parafă)</div>
            </div>
            <div className="col text-center">
              <strong>ȘTAMPILA UNITĂȚII</strong>
              <div className="line border-t border-black mt-16 mx-5" />
            </div>
          </div>

          <div className="aviz-disclaimer mt-5 text-xs italic text-center border-t border-dashed border-slate-400 pt-2">
            Modificarea stării de sănătate poate invalida prezentul aviz medico-sportiv.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConcluziiAvizButton;
